/**
 * Détection appareil (iPhone / Android / iPad) + insets plein écran du signet.
 * Combine UA, Client Hints, dimensions CSS, DPR et safe-area réelle.
 */

const IPHONE_PROFILES = [
  { w: 440, h: 956, dpr: 3, model: 'iPhone 16 Pro Max', top: 62, bottom: 34 },
  { w: 430, h: 932, dpr: 3, model: 'iPhone 15 ou 16 ', top: 59, bottom: 34 },
  { w: 428, h: 926, dpr: 3, model: 'iPhone 14 P / 13PM / 12PM', top: 47, bottom: 34 },
  { w: 414, h: 896, dpr: 3, model: 'iPhone 11 PM / XS M', top: 44, bottom: 34 },
  { w: 414, h: 896, dpr: 2, model: 'iPhone 11 / XR', top: 48, bottom: 34 },
  { w: 414, h: 736, dpr: 3, model: 'iPhone 8P / 7P', top: 20, bottom: 0 },
  { w: 402, h: 874, dpr: 3, model: 'iPhone 16P', top: 62, bottom: 34 },
  { w: 393, h: 852, dpr: 3, model: 'iPhone 15 / 15P / 16', top: 59, bottom: 34 },
  { w: 390, h: 844, dpr: 3, model: 'iPhone (14 / 13 / 12)', top: 47, bottom: 34 },
  { w: 375, h: 812, dpr: 3, model: 'iPhone 13m / 12m / 11P / X / XS', top: 44, bottom: 34 },
  { w: 375, h: 667, dpr: 2, model: 'iPhone SE / 8 / 7', top: 20, bottom: 0 },
  { w: 320, h: 568, dpr: 2, model: 'iPhone SE (1gen)', top: 20, bottom: 0 },
];

const readCssInset = (name) => {
  try {
    const raw = getComputedStyle(document.documentElement).getPropertyValue(name);
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
};

const portraitBox = () => {
  const w = window.screen?.width || window.innerWidth || 0;
  const h = window.screen?.height || window.innerHeight || 0;
  return { w: Math.min(w, h), h: Math.max(w, h), dpr: window.devicePixelRatio || 1 };
};

const matchIphoneProfile = () => {
  const { w, h, dpr } = portraitBox();
  let best = null;
  let bestScore = Infinity;
  IPHONE_PROFILES.forEach((p) => {
    const score = Math.abs(p.w - w) + Math.abs(p.h - h) * 0.35 + Math.abs(p.dpr - dpr) * 8;
    if (score < bestScore) {
      bestScore = score;
      best = p;
    }
  });
  if (!best || bestScore > 28) {
    return {
      model: w >= 428 ? 'iPhone max' : w >= 390 ? 'iPhone' : 'iPhone compact',
      top: h >= 800 ? 47 : 20,
      bottom: h >= 800 ? 34 : 0,
    };
  }
  return { model: best.model, top: best.top, bottom: best.bottom };
};

const parseAndroidModel = (ua) => {
  const patterns = [
    /;\s*([^;)]*?)\s+Build\//i,
    /Android[^;]*;\s*([^;)]+)\)/i,
  ];
  for (const re of patterns) {
    const m = ua.match(re);
    if (m && m[1]) {
      const raw = m[1].replace(/^(?:K|wv|Mobile)\s+/i, '').trim();
      if (raw && raw.length > 1 && raw.length < 48 && !/^Android/i.test(raw)) return raw;
    }
  }
  return 'Android';
};

export const detectDevice = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return { platform: 'unknown', model: 'Smartphone', isStandalone: false, safeTop: 0, safeBottom: 20 };
  }

  const ua = navigator.userAgent || '';
  const platformHint = navigator.userAgentData?.platform || navigator.platform || '';
  const touchPad = navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1;
  const isIos = /iPhone|iPad|iPod/i.test(ua)
    || /iPhone|iPad|iPod/i.test(platformHint)
    || touchPad
    || (/CriOS|FxiOS|EdgiOS/i.test(ua));
  const isAndroid = !isIos && (
    /Android/i.test(ua)
    || /Android/i.test(platformHint)
    || navigator.userAgentData?.platform === 'Android'
  );

  const isStandalone = !!(
    window.matchMedia?.('(display-mode: standalone)')?.matches
    || window.matchMedia?.('(display-mode: fullscreen)')?.matches
    || window.navigator.standalone === true
    || document.referrer.includes('android-app://')
  );

  const envTop = readCssInset('--sat-probe') || 0;
  const envBottom = readCssInset('--sab-probe') || 0;

  if (isIos) {
    if (/iPad/i.test(ua) || touchPad) {
      return {
        platform: 'ios',
        family: 'ipad',
        model: 'iPad',
        isStandalone,
        // Prefer native env() when available — avoid stacking fake + real insets (white bands)
        safeTop: envTop > 0 ? envTop : 24,
        safeBottom: envBottom > 0 ? envBottom : 20,
      };
    }
    const profile = matchIphoneProfile();
    return {
      platform: 'ios',
      family: 'iphone',
      model: profile.model,
      isStandalone,
      safeTop: envTop > 0 ? envTop : profile.top,
      safeBottom: envBottom > 0 ? envBottom : profile.bottom,
    };
  }

  if (isAndroid) {
    const { h } = portraitBox();
    const gesture = h >= 780 ? 28 : 16;
    return {
      platform: 'android',
      family: 'android',
      model: parseAndroidModel(ua),
      isStandalone,
      safeTop: envTop > 0 ? envTop : 24,
      safeBottom: envBottom > 0 ? envBottom : gesture,
    };
  }

  return {
    platform: 'unknown',
    family: 'unknown',
    model: 'Smartphone',
    isStandalone,
    safeTop: Math.max(envTop, 0),
    safeBottom: Math.max(envBottom, 16),
  };
};

export const applyDeviceLayout = () => {
  if (typeof document === 'undefined') return detectDevice();
  const root = document.documentElement;
  if (!root.querySelector('style[data-safe-probe]')) {
    const probe = document.createElement('style');
    probe.setAttribute('data-safe-probe', 'true');
    probe.textContent = `:root{--sat-probe:env(safe-area-inset-top,0px);--sab-probe:env(safe-area-inset-bottom,0px);}`;
    document.head.appendChild(probe);
  }

  const info = detectDevice();
  root.dataset.platform = info.platform;
  root.dataset.deviceModel = info.model;
  root.dataset.standalone = info.isStandalone ? '1' : '0';
  root.classList.toggle('device-ios', info.platform === 'ios');
  root.classList.toggle('device-android', info.platform === 'android');
  root.classList.toggle('device-standalone', !!info.isStandalone);
  root.style.setProperty('--device-safe-top', `${info.safeTop}px`);
  root.style.setProperty('--device-safe-bottom', `${info.safeBottom}px`);
  root.style.setProperty('--gs-safe-top', `max(${info.safeTop}px, env(safe-area-inset-top, 0px))`);
  root.style.setProperty('--gs-safe-bottom', `max(${info.safeBottom}px, env(safe-area-inset-bottom, 0px))`);
  // Bandeaux haut adaptés au modèle (évite collisions Pass / coins / visiteurs / langue)
  const row1 = info.platform === 'ios' ? 6 : 8;
  const row2 = info.platform === 'ios' ? 44 : 48;
  const row3 = info.platform === 'ios' ? 76 : 80;
  const content = info.platform === 'ios' ? 108 : 112;
  root.style.setProperty('--gs-top-row', `calc(var(--gs-safe-top) + ${row1}px)`);
  root.style.setProperty('--gs-top-row-2', `calc(var(--gs-safe-top) + ${row2}px)`);
  root.style.setProperty('--gs-top-row-3', `calc(var(--gs-safe-top) + ${row3}px)`);
  root.style.setProperty('--gs-top-content', `calc(var(--gs-safe-top) + ${content}px)`);
  root.style.setProperty('--gs-top-side-pad', `16px`);
  root.style.setProperty(
    '--device-nav-height',
    `calc(96px + env(safe-area-inset-bottom, ${info.safeBottom}px))`
  );
  return info;
};

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  try { applyDeviceLayout(); } catch { /* silencieux */ }
}

export const refineDeviceModelAsync = async () => {
  try {
    const uaData = navigator.userAgentData;
    if (!uaData?.getHighEntropyValues) return detectDevice();
    const hints = await uaData.getHighEntropyValues(['model', 'platform', 'platformVersion']);
    if (hints?.model) {
      document.documentElement.dataset.deviceModel = hints.model;
    }
    if (/Android/i.test(hints?.platform || '') && hints.model) {
      document.documentElement.dataset.platform = 'android';
      document.documentElement.classList.add('device-android');
    }
  } catch { /* hints indisponibles */ }
  return detectDevice();
};
